import { Button } from "@/components/ui/button";
import { Package, Shield, LogOut } from "lucide-react";
import { StaffActionCards } from "./StaffActionCards";
import { RecentOrders } from "./RecentOrders";
import { Order } from "@/hooks/useOrdersManagement/types";
import { useAuth } from "@/components/auth/AuthProvider";
import { useNavigate } from "react-router-dom";

interface StaffDashboardProps {
  userName: string | null;
  orders: Order[];
  onNewOrder: () => void;
  onPriceCheck: () => void;
  onEmployeeClockIn: () => void;
  onViewAllScannedItems?: () => void;
  onEditOrder: (order: Order) => void;
  onViewProducts: (order: Order) => void;
  onRequestAdminAccess: () => void;
  onNavigateToMissingProducts: () => void;
  isLoading?: boolean;
  error?: any;
}

export const StaffDashboard = ({ 
  userName, 
  orders, 
  onNewOrder, 
  onPriceCheck, 
  onEmployeeClockIn,
  onViewAllScannedItems,
  onEditOrder, 
  onViewProducts, 
  onRequestAdminAccess,
  onNavigateToMissingProducts,
  isLoading = false,
  error 
}: StaffDashboardProps) => {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  
  // Get the best available name for greeting
  const displayName = profile?.firstName || profile?.full_name || userName || 'there';
  
  console.log('🏠 StaffDashboard render:', { 
    userName, 
    displayName,
    ordersCount: orders?.length || 0, 
    isLoading, 
    hasError: !!error,
    orders: orders?.slice(0, 3).map(o => ({ id: o.id, supplier: o.supplierName }))
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Modern Header - Inline */}
        <div className="surface-elevated p-8 mb-8 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Package className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h1 className="text-display-md">
                    Welcome{displayName ? `, ${displayName}` : ''}!
                  </h1>
                  <p className="text-muted-foreground text-heading-sm">Staff Dashboard - Basic Access</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="status-info px-4 py-2 rounded-full">
                <span className="text-sm font-medium">Staff Mode</span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={onRequestAdminAccess}
                className="interactive-hover"
              >
                <Shield className="w-4 h-4" />
                Admin Access
              </Button>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={signOut}
                className="interactive-hover"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
        
        {/* Action Cards */}
        <StaffActionCards 
          onNewOrder={() => navigate('/checkin')}
          onPriceCheck={onPriceCheck}
          onEmployeeClockIn={onEmployeeClockIn}
          onViewAllScannedItems={onViewAllScannedItems}
        />
        
        {/* Recent Orders */}
        <div className="mt-8">
          <RecentOrders 
            orders={orders}
            onCreateFirstOrder={() => navigate('/checkin')}
            onEditOrder={onEditOrder}
            onViewProducts={onViewProducts}
            isLoading={isLoading}
            error={error}
          />
        </div>
      </div>
    </div>
  );
};